import {
    AutocompleteInput,
    Create,
    DateInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import {
    PackagePlus
} from 'lucide-react';
import { useGetOne, useNotify, useRedirect } from 'ra-core';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { AutoReferenceField } from '../documents/shared/AutoReferenceField';
import { supabase } from '../providers/supabase/supabase';

/**
 * POItemsPreview — reads the linked PO and displays imported line items
 * so the user can verify what they are receiving.
 */
const POItemsPreview = ({ poId }: { poId: string | number }) => {
    const { data: po } = useGetOne<any>('purchase_orders', { id: poId });
    if (!po?.items?.length) return null;

    return (
        <div className="md:col-span-2 mt-4">
            <div className="flex items-center gap-3 mb-3">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    Items from PO: {po.reference}
                </h4>
                <div className="flex-1 h-px bg-slate-200" />
                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                    {po.items.length} items
                </Badge>
            </div>
            <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-slate-800 text-white">
                            <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider">#</th>
                            <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider">Description</th>
                            <th className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider">HSN</th>
                            <th className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider">Ordered Qty</th>
                            <th className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider">UoM</th>
                        </tr>
                    </thead>
                    <tbody>
                        {po.items.map((item: any, i: number) => (
                            <tr key={i} className="border-t hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-3 text-slate-400 font-mono text-xs">{i + 1}</td>
                                <td className="px-4 py-3 font-medium text-slate-800">{item.description}</td>
                                <td className="px-4 py-3 text-center font-mono text-xs text-slate-500">{item.hsn_code || '—'}</td>
                                <td className="px-4 py-3 text-center font-bold text-slate-700">{item.quantity}</td>
                                <td className="px-4 py-3 text-center text-slate-500 text-xs">{item.unit || 'Nos'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="bg-blue-50 px-4 py-2 text-xs text-blue-600 font-medium border-t border-blue-100">
                    💡 After saving the GRN, open the receipt to mark individual items as accepted/rejected under the Items &amp; QA tab.
                </div>
            </div>
        </div>
    );
};

/** Watches po_id field and shows item preview reactively */
const POWatcher = ({ defaultPoId }: { defaultPoId?: string | number }) => {
    const { watch } = useFormContext();
    const poId = watch('po_id') || defaultPoId;
    if (!poId) return null;
    return <POItemsPreview poId={poId} />;
};

export const GRNCreate = () => {
    const location = useLocation();
    const stateRecord = location.state?.record || {};
    const notify = useNotify();
    const redirect = useRedirect();

    // After the GRN header is saved, seed grn_items from the linked PO's line
    // items so the "Items & QA" tab has rows to accept/reject. See bug tracker
    // GRN #5.
    const onSuccess = async (data: any) => {
        try {
            if (data.po_id) {
                const { data: poItems } = await supabase
                    .from('po_items')
                    .select('*')
                    .eq('po_id', data.po_id);
                if (poItems?.length) {
                    const rows = poItems.map((it: any) => ({
                        grn_id: data.id,
                        item_id: it.item_id ?? null,
                        po_item_description: it.description,
                        ordered_qty: it.quantity ?? 0,
                        received_qty: it.quantity ?? 0,
                        accepted_qty: 0,
                        rejected_qty: 0,
                        qa_status: 'pending',
                    }));
                    const { error } = await supabase.from('grn_items').insert(rows);
                    if (error) console.error('Failed to seed grn_items:', error);
                }
            }
        } catch (e) {
            console.error('GRN item seeding error:', e);
        }
        notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } });
        redirect(`/goods_receipts/${data.id}/show`);
    };

    return (
        <Create title="Record Goods Receipt (GRN)" mutationOptions={{ onSuccess }}>
            <SimpleForm defaultValues={stateRecord} className="max-w-4xl">
                {/* Header */}
                <div className="mb-6 flex items-center gap-3 text-slate-800 pb-4 border-b">
                    <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center border border-emerald-100">
                        <PackagePlus className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold">New Goods Receipt Note (GRN)</h3>
                        <p className="text-xs text-slate-500">Record goods received against a Purchase Order</p>
                    </div>
                </div>

                {/* Reference + Date row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                    <AutoReferenceField docType="goods_receipt" />
                    <DateInput source="received_date" label="Received Date" required />
                </div>

                {/* PO + Vendor row — disabled if pre-filled from PO */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                    <ReferenceInput source="po_id" reference="purchase_orders" perPage={100} sort={{ field: 'date', order: 'DESC' }}>
                        <AutocompleteInput
                            label="Purchase Order"
                            optionText="reference"
                            isRequired
                            disabled={!!stateRecord.po_id}
                        />
                    </ReferenceInput>

                    <ReferenceInput source="vendor_id" reference="vendors" perPage={100}>
                        <AutocompleteInput
                            label="Vendor"
                            optionText="name"
                            isRequired
                            disabled={!!stateRecord.vendor_id}
                        />
                    </ReferenceInput>
                </div>

                {/* Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                    <SelectInput
                        source="status"
                        isRequired
                        defaultValue="draft"
                        choices={[
                            { id: 'draft',     name: 'Draft — pending verification' },
                            { id: 'received',  name: 'Received — goods on hand' },
                            { id: 'qa_passed', name: 'QA Passed — ready for billing' },
                            { id: 'qa_failed', name: 'QA Failed — return/rework needed' },
                        ]}
                    />
                    <TextInput source="notes" label="Notes / Remarks" multiline rows={3} />
                </div>

                {/* PO items preview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                    <POWatcher defaultPoId={stateRecord.po_id} />
                </div>
            </SimpleForm>
        </Create>
    );
};
